import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { SECTIONS, brandFootprint, emptyField, newId, slugify, type BrandFootprint, type BrandsSection as Doc, type PortalConfig } from '@sonder/shared'
import { useMode } from '@/app/mode'
import { useAllSections } from '@/data/client'
import { useSectionEditor } from '@/data/editor'
import { useCallable } from '@/data/hooks'
import { Button, Field, Input, Modal, useToast } from '@/ui'
import { AddRow, EntityCard, NoEntities, TeamOnlyCard } from './common'
import { FieldEditor } from './FieldEditor'

const DEF = SECTIONS.brands
const F = Object.fromEntries(DEF.fields.map((f) => [f.key, f]))
const NOTES = { key: 'notes', label: 'Notes from kickoff', kind: 'textarea' as const, required: false, confirmable: false, hint: 'Purpose, sensitivities, what they loved. Covers every brand.' }

export function BrandsSection({ clientId, config }: { clientId: string; config: PortalConfig }) {
  const ed = useSectionEditor<Doc>(clientId, 'brands')
  const mode = useMode()
  const [newName, setNewName] = useState('')
  const [removing, setRemoving] = useState<string | null>(null)
  const doc = ed.doc
  if (!doc) return null
  const order = doc.order ?? []

  const add = () => {
    const name = newName.trim()
    if (!name) return
    let id = slugify(name)
    if (doc.items?.[id]) id = `${id}-${newId().slice(0, 4)}`
    const now = Date.now()
    ed.setRaw(`items.${id}`, {
      name: { ...emptyField(name, now), prefilled: mode !== 'client', source: mode === 'client' ? 'client' : 'staff', confirmed: mode === 'client' },
      legal: emptyField('', now), industry: emptyField('', now), background: emptyField('', now), website: emptyField('', now),
      tone: emptyField('', now), themes: emptyField('', now), avoid: emptyField('', now), createdAt: now,
    })
    ed.setRaw('order', [...order, id])
    setNewName('')
  }

  return (
    <div className="grid gap-12">
      {order.length === 0 && <NoEntities what="brands" hint={mode === 'client' ? 'Your Sonder contact will set these up.' : 'Add each concept below; every later section builds on this list.'} />}
      {order.map((id, i) => (
        <EntityCard key={id} eyebrow={`Brand ${i + 1}`} title={doc.items[id]?.name.value || 'Untitled brand'} onRemove={mode === 'prep' ? () => setRemoving(id) : undefined} removeLabel="Remove brand">
          {DEF.fields.map((f) => (
            <FieldEditor key={f.key} editor={ed} path={`items.${id}.${f.key}`} def={F[f.key]!} rows={f.key === 'background' ? 5 : 3} />
          ))}
        </EntityCard>
      ))}
      {mode !== 'preview' && (
        <AddRow label="Add brand" onAdd={add}>
          <Field label="New brand name" labelHidden className="min-w-64">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Brand name" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())} />
          </Field>
        </AddRow>
      )}
      {mode === 'prep' && (
        <TeamOnlyCard title="Kickoff notes">
          <FieldEditor editor={ed} path="notes" def={NOTES} rows={6} />
        </TeamOnlyCard>
      )}
      <RemoveBrandDialog key={removing ?? 'closed'} clientId={clientId} config={config} brandId={removing} name={(removing && doc.items[removing]?.name.value) || 'Untitled brand'} onClose={() => setRemoving(null)} />
    </div>
  )
}

/** Case and extra spaces don't matter; the characters do. */
const normalize = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase()

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`

/** Plain-language list of what goes with the brand; only things someone actually filled in. */
function footprintLines(fp: BrandFootprint): string[] {
  const lines: string[] = []
  if (fp.locations.length) lines.push(`${plural(fp.locations.length, 'location')} and ${fp.locations.length === 1 ? 'its' : 'their'} hours: ${fp.locations.map((l) => l.name).join(', ')}`)
  if (fp.audienceAnswered) lines.push('Audience answers')
  if (fp.accountsAnswered) lines.push(`${plural(fp.accountsAnswered, 'social account')}${fp.secretIds.length ? `, including ${plural(fp.secretIds.length, 'saved login')}` : ''}`)
  if (fp.keyLinksAnswered) lines.push('Key links')
  if (fp.brandKitAnswered) lines.push('Brand kit links and notes')
  if (fp.faqAnswered) lines.push('FAQ answers')
  if (fp.uploads.length) lines.push(plural(fp.uploads.length, 'uploaded file'))
  return lines
}

function RemoveBrandDialog({ clientId, config, brandId, name, onClose }: { clientId: string; config: PortalConfig; brandId: string | null; name: string; onClose: () => void }) {
  const { docs } = useAllSections(clientId)
  const lines = useMemo(() => (brandId ? footprintLines(brandFootprint(docs, config, brandId)) : []), [docs, config, brandId])
  const removeBrand = useCallable<{ clientId: string; brandId: string }, { ok: true }>('removeBrand')
  const { toast } = useToast()
  const [busy, setBusy] = useState(false)
  const [typed, setTyped] = useState('')
  const matches = normalize(typed) === normalize(name)
  const formId = useId()
  const input = useRef<HTMLInputElement>(null)
  // Remounted per brand (keyed by the parent); focus the name box once the dialog is showing.
  useEffect(() => {
    if (brandId) input.current?.focus()
  }, [brandId])
  const confirm = async () => {
    if (!brandId || !matches) return
    setBusy(true)
    try {
      await removeBrand({ clientId, brandId })
      toast(`${name} removed`, 'success')
      onClose()
    } catch (e) {
      toast((e as Error).message || 'Could not remove the brand', 'error')
    } finally {
      setBusy(false)
    }
  }
  return (
    <Modal
      open={brandId !== null}
      onClose={() => !busy && onClose()}
      title={`Remove ${name}?`}
      footer={
        <>
          <Button variant="ghost" arrow={false} onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="danger" arrow={false} type="submit" form={formId} disabled={busy || !matches}>{busy ? 'Removing…' : 'Remove brand'}</Button>
        </>
      }
    >
      <form id={formId} onSubmit={(e) => { e.preventDefault(); void confirm() }} className="grid gap-4 text-body">
        {lines.length > 0 ? (
          <>
            <p>This also removes everything entered for this brand across the onboarding:</p>
            <ul className="grid gap-2 border-l-2 border-danger pl-4">
              {lines.map((l) => <li key={l}>{l}</li>)}
            </ul>
          </>
        ) : (
          <p>Nothing else has been filled in for this brand yet.</p>
        )}
        <p className="text-small text-dim">This can't be undone.</p>
        <div className="grid gap-2 border-t border-gray-200 pt-4">
          <p id={`${formId}-ask`}>
            To confirm, type <strong className="font-semibold text-danger">{name}</strong> below.
          </p>
          <Field label="Brand name to confirm" labelHidden>
            <Input ref={input} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} disabled={busy} aria-describedby={`${formId}-ask`} />
          </Field>
        </div>
      </form>
    </Modal>
  )
}
